/**
 * Edición de un crédito (Gestión → Créditos).
 *
 * Porta el modal «Actualización de Crédito» del panel anterior
 * (`/API/credit/update`), que se abría con el lápiz de cada fila:
 *
 * - **Límite de crédito** → `credito.total`, el importe de la línea.
 * - **Estado del crédito** → `credito_estado_id` (catálogo `abc_credito_estado`).
 * - **¿Aplica descuento?** → `credito.aplicar_descuento` (`SI`/`NO`). Al activarlo
 *   se muestra un campo por **producto del catálogo de combustibles** con su
 *   descuento (puede ser negativo).
 * - **Código externo de EDSuite** → `credito.external_code_edsuite` (opcional).
 *
 * Reglas conservadas: sólo se guardan los descuentos **distintos de cero**, si el
 * crédito no aplica descuentos la columna JSON queda vacía, y en los créditos de
 * **precio fijo** el descuento lo calcula el sistema, así que la sección no se
 * edita.
 */

import { Info, Lock, Percent, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { ApiError } from '@/lib/http/api-error';
import { cn } from '@/lib/utils';

import {
  useActualizarCredito,
  useCredito,
  useCreditosCatalogos,
  useEliminarCredito,
  useFlexibilidad,
  useRegistrarFlexibilidad,
} from '../cierre.api';
import type { DescuentoCredito, FiltrosCreditos } from '../cierre-gestion.types';
import { fechaCorta, monto } from '../gestion-formato';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

interface Props {
  id: number | null;
  filtros: FiltrosCreditos;
  onClose: () => void;
  onAviso: (mensaje: string) => void;
  /** Se llama tras eliminar la línea para refrescar el listado. */
  onEliminado: () => void;
}

export function CreditoEditarDialog({ id, filtros, onClose, onAviso, onEliminado }: Props) {
  const consulta = useCredito(id, filtros);
  const catalogos = useCreditosCatalogos();
  const actualizar = useActualizarCredito();
  const flexibilidad = useFlexibilidad(id);
  const agregarFlexibilidad = useRegistrarFlexibilidad(id);
  const eliminar = useEliminarCredito();
  const [nuevaFlexibilidad, setNuevaFlexibilidad] = useState('');
  const [confirmarBaja, setConfirmarBaja] = useState(false);

  const credito = consulta.data?.credito ?? null;
  const [limite, setLimite] = useState('');
  const [estado, setEstado] = useState('');
  const [aplica, setAplica] = useState<'SI' | 'NO'>('NO');
  const [codigo, setCodigo] = useState('');
  const [descuentos, setDescuentos] = useState<Record<number, string>>({});
  const [listo, setListo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Al llegar el detalle se rellena el formulario (una sola vez por crédito).
  if (credito && !listo) {
    setLimite(String(credito.limite));
    setEstado(String(credito.credito_estado_id ?? ''));
    setAplica(credito.aplica_descuento ? 'SI' : 'NO');
    setCodigo(credito.external_code_edsuite ?? '');

    const mapa: Record<number, string> = {};

    for (const descuento of credito.descuentos) {
      mapa[descuento.producto_id] = String(descuento.descuento);
    }

    setDescuentos(mapa);
    setListo(true);
  }

  const esPrecioFijo = credito?.tipo_credito === 'CREDITO POR PRECIO FIJO';
  const productos = catalogos.data?.productos ?? [];

  /** Productos que se muestran: todos, o sólo el del crédito si lo tiene fijado. */
  const visibles = credito?.producto_id
    ? productos.filter((producto) => producto.id === credito.producto_id)
    : productos;

  const guardar = () => {
    if (!credito) return;

    setError(null);

    const lista: DescuentoCredito[] = [];

    if (aplica === 'SI') {
      for (const producto of visibles) {
        const valor = Number(descuentos[producto.id] ?? 0);

        if (Number.isFinite(valor) && valor !== 0) {
          lista.push({ producto_id: producto.id, descuento: valor });
        }
      }
    }

    actualizar.mutate(
      {
        id: credito.id,
        datos: {
          total: Number(limite),
          credito_estado_id: Number(estado),
          aplicar_descuento: aplica,
          external_code_edsuite: codigo.trim(),
          descuento: lista,
        },
      },
      {
        onSuccess: (respuesta) => {
          onClose();
          onAviso(respuesta.mensaje);
        },
        onError: (fallo) =>
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudo actualizar el crédito.'),
      },
    );
  };

  return (
    <Dialog
      open={id !== null}
      onClose={onClose}
      size="lg"
      title={credito ? `Editar crédito · ${credito.cliente}` : 'Editar crédito'}
      description={
        credito
          ? `${credito.numero_doc} · ${credito.tipo_credito ?? ''} · ${credito.producto}`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button size="sm" loading={actualizar.isPending} disabled={!credito} onClick={guardar}>
            Guardar cambios
          </Button>
        </>
      }
    >
      {consulta.isLoading ? <Skeleton className="h-72 w-full" /> : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo abrir el crédito">
          {consulta.error instanceof ApiError ? consulta.error.message : 'Inténtalo de nuevo.'}
        </Alert>
      ) : null}

      {credito ? (
        <div className="space-y-4">
          {error ? (
            <Alert variant="error" title="No se pudo guardar">
              {error}
            </Alert>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Línea de crédito (S/)"
              type="number"
              min={0}
              step="0.01"
              value={limite}
              onChange={(evento) => setLimite(evento.target.value)}
            />

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Estado del crédito</span>
              <Select
                className={CONTROL}
                value={estado}
                onChange={(evento) => setEstado(evento.target.value)}
              >
                <option value="">Sin estado</option>
                {(catalogos.data?.estados ?? []).map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.descripcion}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">¿Aplica descuento?</span>
              <Select
                className={CONTROL}
                value={aplica}
                disabled={esPrecioFijo}
                onChange={(evento) => setAplica(evento.target.value as 'SI' | 'NO')}
              >
                <option value="NO">No aplica</option>
                <option value="SI">Sí aplica</option>
              </Select>
            </label>

            <Input
              label="Código externo de EDSuite"
              placeholder="Opcional, para centralización"
              value={codigo}
              onChange={(evento) => setCodigo(evento.target.value)}
            />
          </div>

          <p className="text-xs text-ink-500">
            La línea del cliente es esta línea más su flexibilidad: {credito.linea.toLocaleString('es-PE')} en
            total ({credito.limite.toLocaleString('es-PE')} + {credito.flexibilidad.toLocaleString('es-PE')}).
          </p>

          {esPrecioFijo ? (
            <Alert variant="info" title="Crédito por precio fijo">
              <span className={ICONO_EN_ALERTA}>
                <Lock className="size-4" aria-hidden="true" />
                En los créditos de precio fijo el descuento lo calcula el sistema, así que no se
                edita aquí.
              </span>
            </Alert>
          ) : (
            <section className="rounded-card border border-ink-100 bg-ink-50/40 p-4">
              <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-ink-800">
                <Percent className="size-4 text-brand-500" aria-hidden="true" />
                Descuentos por producto
              </h3>

              <p className="mb-3 text-xs text-ink-500">
                Valores positivos son descuentos y los negativos, recargos. Se guardan sólo los
                productos con un valor distinto de cero.
              </p>

              {aplica === 'NO' ? (
                <p className="text-sm text-ink-500">
                  El crédito no aplica descuentos; al guardar se limpian los que tuviera.
                </p>
              ) : visibles.length === 0 ? (
                <p className="text-sm text-ink-500">No hay productos en el catálogo de combustibles.</p>
              ) : (
                <ul className="grid gap-3 sm:grid-cols-2">
                  {visibles.map((producto) => (
                    <li key={producto.id} className="flex items-center gap-3">
                      <span className="min-w-0 flex-1 truncate text-sm text-ink-800">
                        {producto.nombre}
                      </span>
                      <input
                        className={cn(CONTROL, 'w-28 text-right')}
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={descuentos[producto.id] ?? ''}
                        onChange={(evento) =>
                          setDescuentos((previo) => ({
                            ...previo,
                            [producto.id]: evento.target.value,
                          }))
                        }
                      />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          <section className="rounded-card border border-ink-100 bg-white p-4">
            <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-ink-800">
              <Percent className="size-4 text-brand-500" aria-hidden="true" />
              Flexibilidad
            </h3>

            <p className="mb-3 text-xs text-ink-500">
              Flexibilidad actual: <b>{monto(flexibilidad.data?.flexibilidad ?? credito.flexibilidad)}</b>.
              Cada adición queda registrada con su usuario y fecha, como en el panel anterior.
            </p>

            <div className="flex flex-wrap items-end gap-3">
              <Input
                label="Añadir flexibilidad (S/)"
                type="number"
                min={0}
                step="0.01"
                containerClassName="w-44"
                value={nuevaFlexibilidad}
                onChange={(evento) => setNuevaFlexibilidad(evento.target.value)}
              />
              <Button
                variant="outline"
                size="sm"
                loading={agregarFlexibilidad.isPending}
                disabled={!(Number(nuevaFlexibilidad) > 0)}
                onClick={() =>
                  agregarFlexibilidad.mutate(Number(nuevaFlexibilidad), {
                    onSuccess: (respuesta) => {
                      setNuevaFlexibilidad('');
                      onAviso(respuesta.mensaje);
                    },
                    onError: (fallo) =>
                      setError(
                        fallo instanceof ApiError ? fallo.message : 'No se pudo registrar la flexibilidad.',
                      ),
                  })
                }
              >
                Añadir
              </Button>
            </div>

            {flexibilidad.data?.movimientos.length ? (
              <ul className="mt-3 max-h-32 divide-y divide-ink-100 overflow-auto rounded-control border border-ink-100 text-sm">
                {flexibilidad.data.movimientos.map((movimiento) => (
                  <li key={movimiento.id} className="flex items-center justify-between gap-3 px-3 py-1.5">
                    <span className="text-ink-600">
                      {movimiento.detalle ?? '—'} · {fechaCorta(movimiento.fecha)}
                      {movimiento.usuario ? ` · ${movimiento.usuario}` : ''}
                    </span>
                    <span className="font-semibold text-ink-800">{monto(movimiento.cantidad)}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>

          <section className="rounded-card border border-danger-100 bg-danger-50/40 p-4">
            <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-ink-800">
              <Trash2 className="size-4 text-danger-500" aria-hidden="true" />
              Eliminar la línea
            </h3>
            <p className="mb-3 text-xs text-ink-500">
              Sólo se puede eliminar una línea que todavía no tenga vales registrados.
            </p>
            <Button variant="danger" size="sm" onClick={() => setConfirmarBaja(true)}>
              Eliminar crédito
            </Button>
          </section>

          <Alert variant="info" title="Cómo se aplica">
            <span className={ICONO_EN_ALERTA}>
              <Info className="size-4" aria-hidden="true" />
              El descuento por producto se usa al despachar contra el crédito; el límite y el estado
              son los que gobiernan si el cliente puede seguir consumiendo.
            </span>
          </Alert>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmarBaja}
        title="Eliminar la línea de crédito"
        description={
          credito
            ? `Se eliminará la línea de ${credito.cliente}. No se puede deshacer.`
            : 'Se eliminará la línea de crédito.'
        }
        confirmLabel="Eliminar"
        loading={eliminar.isPending}
        onClose={() => setConfirmarBaja(false)}
        onConfirm={() => {
          if (id === null) return;

          eliminar.mutate(id, {
            onSuccess: (respuesta) => {
              setConfirmarBaja(false);
              onEliminado();
              onClose();
              onAviso(respuesta.mensaje);
            },
            onError: (fallo) => {
              setConfirmarBaja(false);
              setError(fallo instanceof ApiError ? fallo.message : 'No se pudo eliminar el crédito.');
            },
          });
        }}
      />
    </Dialog>
  );
}

